import React, { useState, useCallback, useEffect, useMemo } from 'react';
import { Box, Alert, Link, Skeleton, Button } from '@mui/material';
import {
  SecondaryToolbar,
  ToolbarSearch,
  ToolbarSelect,
  ToolbarCheckbox,
  ToolbarDate,
  ToolbarSpacer,
  ToolbarCount,
} from '../components/toolbar';
import {
  DataTableCard,
  DataTable,
  DataTableHeadCell,
  DataTableRow,
  DataTableCell,
  DataTableEmpty,
  DataTablePagination,
} from '../components/table';
import { useNavigate } from 'react-router-dom';
import { fetchPayments } from '../services/paymentsApi';
import type {
  PaymentsListItem,
  PaymentsSortField,
} from '../services/paymentsApi';

const currencyFormatter = new Intl.NumberFormat('en-AU', {
  style: 'currency',
  currency: 'AUD',
});

const formatCurrency = (n: number | null | undefined): string =>
  currencyFormatter.format(Number(n) || 0);

const formatDate = (value: string | null): string => {
  if (!value) return '—';
  try {
    return new Date(value).toLocaleDateString('en-AU', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
    });
  } catch {
    return value;
  }
};

// Default the "From" filter to 90 days ago so the page lands on a
// recent slice instead of trying to render every payment ever — DV's
// $count caps at 5000, so an unfiltered first paint underreports the
// total. Users can widen by clearing the date.
const ninetyDaysAgoIso = (): string => {
  const d = new Date();
  d.setDate(d.getDate() - 90);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
};

// Static list of methods supported by RS in this tenant. Could be loaded
// from /api/payment-methods later — keeping it inline avoids a second
// round-trip on first render, and the list is stable.
const PAYMENT_METHOD_OPTIONS = ['Cash', 'EFTPOS', 'Square', 'XeroImport'];

// Kit ToolbarSelect options — empty value is the "any method" default.
const PAYMENT_METHOD_SELECT_OPTIONS = [
  { value: '', label: 'Any' },
  ...PAYMENT_METHOD_OPTIONS.map((m) => ({ value: m, label: m })),
];

const columnLabels: Record<PaymentsSortField, string> = {
  createdAt: 'Created',
  updatedAt: 'Updated',
  amount: 'Amount',
};

interface UserInfo {
  displayName: string;
  userPrincipalName: string;
  roles: string[];
}

interface PaymentsPageProps {
  userInfo?: UserInfo | null;
}

export const PaymentsPage: React.FC<PaymentsPageProps> = ({ userInfo }) => {
  const navigate = useNavigate();

  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(50);

  const [sort, setSort] = useState<PaymentsSortField>('createdAt');
  const [order, setOrder] = useState<'asc' | 'desc'>('desc');

  const [searchInput, setSearchInput] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [paymentMethod, setPaymentMethod] = useState('');
  const [createdFrom, setCreatedFrom] = useState(ninetyDaysAgoIso);
  const [createdTo, setCreatedTo] = useState('');
  const [includeDeleted, setIncludeDeleted] = useState(false);

  const [items, setItems] = useState<PaymentsListItem[]>([]);
  const [totalItems, setTotalItems] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [retryNonce, setRetryNonce] = useState(0);

  const isAdmin = userInfo?.roles.includes('admin') ?? false;

  // Debounce the search box so we don't refetch on every keystroke.
  useEffect(() => {
    const t = setTimeout(() => setDebouncedSearch(searchInput.trim()), 300);
    return () => clearTimeout(t);
  }, [searchInput]);

  // Reset to page 0 whenever any filter changes — otherwise the user could
  // land on an out-of-range page after the result set shrinks.
  useEffect(() => {
    setPage(0);
  }, [debouncedSearch, paymentMethod, createdFrom, createdTo, includeDeleted, sort, order]);

  useEffect(() => {
    const controller = new AbortController();
    setLoading(true);
    setError(null);

    fetchPayments({
      page: page + 1,
      pageSize: rowsPerPage,
      sort,
      order,
      search: debouncedSearch || undefined,
      paymentMethod: paymentMethod || undefined,
      createdFrom: createdFrom || undefined,
      createdTo: createdTo || undefined,
      includeDeleted: includeDeleted || undefined,
      signal: controller.signal,
    })
      .then((res) => {
        setItems(res.items);
        setTotalItems(res.pagination.totalItems);
      })
      .catch((err) => {
        if (err instanceof DOMException && err.name === 'AbortError') return;
        setError(err instanceof Error ? err.message : String(err));
      })
      .finally(() => setLoading(false));

    return () => controller.abort();
  }, [
    page,
    rowsPerPage,
    sort,
    order,
    debouncedSearch,
    paymentMethod,
    createdFrom,
    createdTo,
    includeDeleted,
    retryNonce,
  ]);

  const handleSortClick = useCallback((field: PaymentsSortField) => {
    setSort((prevField) => {
      if (prevField === field) {
        setOrder((prevOrder) => (prevOrder === 'asc' ? 'desc' : 'asc'));
        return prevField;
      }
      setOrder('desc');
      return field;
    });
  }, []);

  const handleRetry = () => setRetryNonce((n) => n + 1);

  const renderRsPaymentLink = (payment: PaymentsListItem) => {
    if (!payment.rsId) return null;
    return (
      <Link
        href={`https://techsolvers.repairshopr.com/payments/${payment.rsId}`}
        target="_blank"
        rel="noopener noreferrer"
        underline="hover"
        sx={{ fontSize: '0.8rem' }}
        onClick={(e) => e.stopPropagation()}
      >
        RS
      </Link>
    );
  };

  const showingStart = totalItems === 0 ? 0 : page * rowsPerPage + 1;
  const showingEnd = Math.min(totalItems, (page + 1) * rowsPerPage);

  const skeletonRows = useMemo(
    () =>
      Array.from({ length: 8 }).map((_, i) => (
        <DataTableRow key={`skel-${i}`}>
          {Array.from({ length: 7 }).map((_, j) => (
            <DataTableCell key={j}>
              <Skeleton width="80%" />
            </DataTableCell>
          ))}
        </DataTableRow>
      )),
    [],
  );

  return (
    <>
      {/* Contextual toolbar — floating card on the grey page surface
          (main-content--payments), matching the Invoices page. */}
      <SecondaryToolbar floating>
        <ToolbarSearch
          searchValue={searchInput}
          onSearchChange={setSearchInput}
          searchPlaceholder="Search by customer, ref, or invoice number…"
        />
        <ToolbarSelect
          label="Method"
          value={paymentMethod}
          onChange={setPaymentMethod}
          options={PAYMENT_METHOD_SELECT_OPTIONS}
        />
        <ToolbarDate label="From" value={createdFrom} onChange={setCreatedFrom} />
        <ToolbarDate label="To" value={createdTo} onChange={setCreatedTo} />
        {isAdmin && (
          <ToolbarCheckbox
            label="Deleted"
            active={includeDeleted}
            onClick={() => setIncludeDeleted((v) => !v)}
          />
        )}
        <ToolbarSpacer />
        <ToolbarCount>
          {totalItems === 0
            ? 'No results'
            : `Showing ${showingStart}–${showingEnd} of ${totalItems}`}
        </ToolbarCount>
      </SecondaryToolbar>

      <Box sx={{ p: '12px 16px 24px', maxWidth: 1600, mx: 'auto' }}>
        {error && (
          <Alert
            severity="error"
            sx={{ mb: 2 }}
            action={
              <Button color="inherit" size="small" onClick={handleRetry}>
                Retry
              </Button>
            }
          >
            {error}
          </Alert>
        )}

        <DataTableCard>
          <DataTable fixed>
            <thead>
              <tr>
                <DataTableHeadCell
                  width={120}
                  sortable
                  active={sort === 'createdAt'}
                  direction={order}
                  onSort={() => handleSortClick('createdAt')}
                >
                  {columnLabels.createdAt}
                </DataTableHeadCell>
                <DataTableHeadCell width={300}>Customer</DataTableHeadCell>
                <DataTableHeadCell>Invoice</DataTableHeadCell>
                <DataTableHeadCell width={120}>Method</DataTableHeadCell>
                <DataTableHeadCell
                  width={120}
                  align="right"
                  sortable
                  active={sort === 'amount'}
                  direction={order}
                  onSort={() => handleSortClick('amount')}
                >
                  {columnLabels.amount}
                </DataTableHeadCell>
                <DataTableHeadCell width={130}>Ref</DataTableHeadCell>
                <DataTableHeadCell width={60}>RS</DataTableHeadCell>
              </tr>
            </thead>
            <tbody>
              {loading && items.length === 0
                ? skeletonRows
                : items.map((payment) => (
                    <DataTableRow key={payment.id ?? payment.rsId ?? Math.random()}>
                      <DataTableCell>{formatDate(payment.createdAt)}</DataTableCell>
                      <DataTableCell>{payment.customerName || '—'}</DataTableCell>
                      <DataTableCell>
                        {payment.invoiceNumber ? (
                          <Link
                            component="button"
                            type="button"
                            onClick={() => navigate(`/invoice/${payment.invoiceNumber}`)}
                            underline="hover"
                            sx={{ fontSize: '0.8rem' }}
                            title={payment.invoiceName || `#${payment.invoiceNumber}`}
                          >
                            {payment.invoiceName || `#${payment.invoiceNumber}`}
                          </Link>
                        ) : (
                          '—'
                        )}
                      </DataTableCell>
                      <DataTableCell>{payment.paymentMethod || '—'}</DataTableCell>
                      <DataTableCell align="right">
                        {formatCurrency(payment.amount)}
                      </DataTableCell>
                      <DataTableCell>{payment.refNum || '—'}</DataTableCell>
                      <DataTableCell>{renderRsPaymentLink(payment) ?? '—'}</DataTableCell>
                    </DataTableRow>
                  ))}
              {!loading && items.length === 0 && (
                <DataTableEmpty colSpan={7}>
                  No payments match the current filters.
                </DataTableEmpty>
              )}
            </tbody>
          </DataTable>
          <DataTablePagination
            page={page}
            rowsPerPage={rowsPerPage}
            count={totalItems}
            rowsPerPageOptions={[25, 50, 100, 200]}
            onPageChange={setPage}
            onRowsPerPageChange={(rpp) => {
              setRowsPerPage(rpp);
              setPage(0);
            }}
          />
        </DataTableCard>
      </Box>
    </>
  );
};
